<template>
  <v-dialog v-model="dialog" max-width="640">
    <v-card v-if="user" class="pa-4">
      <v-card-title class="pa-0 ma-0 align-center d-flex justify-space-between text-wrap">
        <span class="text-h6">Delete account {{ user.id }}?</span>
        <v-btn variant="text" icon color="grey" :disabled="deleting" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="pa-0">{{ userLabel }}</v-card-subtitle>
      <v-card-text class="pa-0 mt-6">
        <v-alert type="error" variant="tonal" density="compact" class="mb-4">
          This cannot be undone. The login, the profile and every project the user owns, with all its workflows, are deleted.
        </v-alert>

        <p class="font-weight-bold mb-1">Projects the user owns</p>
        <ul v-if="ownedProjects.length" class="project-list mb-4">
          <li v-for="project in ownedProjects" :key="project.id">
            {{ project.name }} - {{ otherMembersLabel(project) }}
          </li>
        </ul>
        <p v-else class="mb-4">None</p>

        <p class="mb-4">
          <span class="font-weight-bold">Subscription:</span> {{ subscriptionLabel }}
        </p>

        <v-text-field
          v-model="confirmation"
          label="To confirm deletion, enter the user ID"
          variant="outlined"
          density="compact"
          hide-details
          data-cy="admin-delete-account-input"
        />

        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
          data-cy="admin-delete-account-error"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions class="pa-0 mt-6 justify-end">
        <v-btn size="large" variant="outlined" :disabled="deleting" @click="dialog = false">
          Cancel
        </v-btn>
        <v-btn
          size="large"
          variant="flat"
          color="primary"
          :loading="deleting"
          :disabled="confirmation !== user.id"
          data-cy="admin-delete-account-confirm-btn"
          @click="deleteAccount"
        >
          Delete account
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import client from '@/graphql/client'
import DeleteUserAccount from '@/graphql/mutations/DeleteUserAccount'
import { ENTERPRISE_CUSTOMER_ID_PREFIX } from '@/utils/constants'

const dialog = defineModel({ type: Boolean, default: false })
const props = defineProps({
  user: {
    type: Object,
    default: null
  }
})
const emit = defineEmits(['deleted'])

const store = useStore()

const confirmation = ref('')
const errorMessage = ref('')
const deleting = ref(false)

const pluralMembers = (count) => `${count} other ${count === 1 ? 'member' : 'members'}`

const userLabel = computed(() => {
  const name = [props.user?.firstname, props.user?.lastname].filter(Boolean).join(' ')

  return [name, props.user?.email].filter(Boolean).join(' · ')
})

const ownedProjects = computed(() => {
  return (props.user?.organisations || []).flatMap((organisation) => organisation.projects || [])
})

const otherMembersLabel = (project) => {
  const count = (project.teamMembers?.items || []).filter((member) => member.userId !== props.user.id).length

  return count ? pluralMembers(count) : 'no other members'
}

const subscriptionLabel = computed(() => {
  const subscriptions = store.state.subscriptions.subscriptions || []
  const row = subscriptions.find((subscription) => subscription.email === props.user?.email)

  if (!row) {
    return 'none'
  }

  const payer = row.payerUser
  const payerIsEnterprise = Boolean(payer?.stripeCustomerId?.startsWith(ENTERPRISE_CUSTOMER_ID_PREFIX))

  if (payer?.id === props.user.id) {
    const members = subscriptions.filter((subscription) => {
      return subscription.payerUser?.id === props.user.id && subscription.email !== row.email
    }).length

    return `pays for ${payerIsEnterprise ? 'an enterprise plan' : 'a Stripe plan'} with ${pluralMembers(members)}`
  }

  const planName = row.enterpriseCustomerId || payerIsEnterprise ? 'an enterprise plan' : 'a Stripe plan'

  return payer?.email ? `on ${planName} paid by ${payer.email}` : `on ${planName}`
})

const deleteAccount = async () => {
  deleting.value = true
  errorMessage.value = ''

  try {
    const res = await client.query({
      operationName: 'deleteUserAccount',
      query: DeleteUserAccount,
      variables: { userId: props.user.id }
    })
    const { blockedReason } = res.deleteUserAccount

    if (blockedReason) {
      errorMessage.value = blockedReason

      return
    }

    emit('deleted', props.user.id)
    dialog.value = false
  } catch (error) {
    errorMessage.value = error.message.replace(/^GraphQL error: /, '')
  } finally {
    deleting.value = false
  }
}

watch(dialog, (open) => {
  if (open) {
    confirmation.value = ''
    errorMessage.value = ''
  }
})
</script>

<style scoped>
.project-list {
  padding-left: 20px;
}
</style>
